import { Link } from "@tanstack/react-router";
import {
  BatteryCharging,
  Droplet,
  Flame,
  HelpCircle,
  Sun,
  type LucideIcon,
} from "lucide-react";

type RouteCard =
  | {
      kind: "link";
      Icon: LucideIcon;
      title: string;
      body: string;
      cta: string;
      to: "/boiler-upgrade-scheme" | "/solar-pv";
    }
  | {
      kind: "scroll";
      Icon: LucideIcon;
      title: string;
      body: string;
      cta: string;
    };

const ROUTES: RouteCard[] = [
  {
    kind: "link",
    Icon: Flame,
    title: "I Have Gas Heating",
    body: "You may be able to access the £7,500 BUS grant towards a heat pump, subject to eligibility.",
    cta: "Check Gas Route",
    to: "/boiler-upgrade-scheme",
  },
  {
    kind: "link",
    Icon: Droplet,
    title: "I Have Oil Or LPG Heating",
    body: "Eligible oil and LPG heated homes can access a £9,000 grant from 21 July 2026 to 31 March 2027, subject to scheme rules.",
    cta: "Check Oil / LPG Route",
    to: "/boiler-upgrade-scheme",
  },
  {
    kind: "link",
    Icon: Sun,
    title: "I Want Solar Panels",
    body: "Generate your own electricity and reduce grid reliance.",
    cta: "Explore Solar",
    to: "/solar-pv",
  },
  {
    kind: "link",
    Icon: BatteryCharging,
    title: "I Want Battery Storage",
    body: "Store more of the electricity your home generates.",
    cta: "Explore Battery",
    to: "/solar-pv",
  },
  {
    kind: "scroll",
    Icon: HelpCircle,
    title: "I'm Not Sure What I Need",
    body: "Start with a free home energy survey.",
    cta: "Help Me Choose",
  },
];

function scrollToHomeHeroForm() {
  document.getElementById("home-hero-form")?.scrollIntoView({ behavior: "smooth", block: "start" });
}

const cardClassName =
  "rounded-2xl border bg-card p-5 flex flex-col text-left hover:border-accent/50 hover:shadow-soft transition-all";

function RouteCardContent({ route }: { route: RouteCard }) {
  return (
    <>
      <div className="w-11 h-11 rounded-xl bg-accent/10 flex items-center justify-center mb-3">
        <route.Icon className="h-5 w-5 text-accent" />
      </div>
      <h3 className="font-bold text-foreground text-sm md:text-base mb-1.5 leading-tight">
        {route.title}
      </h3>
      <p className="text-xs text-muted-foreground leading-relaxed flex-1">{route.body}</p>
      <span className="mt-4 text-xs font-bold text-accent">{route.cta} →</span>
    </>
  );
}

/** Homepage-only route picker (moved from BUS landing). */
export function HomeRoutePicker() {
  return (
    <section className="py-12 md:py-20 bg-background">
      <div className="container-page">
        <div className="text-center mb-10 max-w-2xl mx-auto">
          <h2 className="text-2xl md:text-3xl font-bold text-foreground mb-3">
            Which Route Sounds Most Like Your Home?
          </h2>
          <p className="text-muted-foreground text-sm md:text-base">
            Choose the closest option, we&apos;ll confirm the right route during your free survey.
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
          {ROUTES.map((route) =>
            route.kind === "link" ? (
              <Link key={route.title} to={route.to} className={cardClassName}>
                <RouteCardContent route={route} />
              </Link>
            ) : (
              <button
                key={route.title}
                type="button"
                onClick={scrollToHomeHeroForm}
                className={cardClassName}
              >
                <RouteCardContent route={route} />
              </button>
            )
          )}
        </div>
      </div>
    </section>
  );
}
